import axios, { AxiosRequestConfig } from 'axios';

/**
 * In development, CRA `setupProxy` maps `/duda-api` → `https://api.duda.co/api` (avoids CORS).
 * In production, this hits Duda directly from the browser (often blocked — use a backend proxy).
 * @see https://developer.duda.co/reference/getting-started-with-the-duda-api
 */
export function getDudaApiBase(): string {
  if (process.env.NODE_ENV === 'development') {
    return '/duda-api';
  }
  return 'https://api.duda.co/api';
}

function utf8ToBase64(s: string): string {
  return btoa(unescape(encodeURIComponent(s)));
}

export function dudaAuthHeader(apiUser: string, apiPassword: string): string {
  const pair = `${apiUser.trim()}:${apiPassword.trim()}`;
  return `Basic ${utf8ToBase64(pair)}`;
}

export type DudaDateGranularity = 'DAYS' | 'MONTHS' | 'WEEKS';

export type DudaSiteSummary = {
  site_name: string;
  site_domain: string;
  account_name?: string;
  external_uid?: string;
};

function normalizeSiteRow(raw: unknown): DudaSiteSummary | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const site_name = String(o.site_name ?? o.name ?? o.siteName ?? '').trim();
  if (!site_name) {
    return null;
  }
  return {
    site_name,
    site_domain: String(o.canonical_domain ?? o.domain ?? o.site_domain ?? o.preview_site_url ?? ''),
    account_name: o.account_name != null ? String(o.account_name) : undefined,
    external_uid: o.external_uid != null ? String(o.external_uid) : undefined,
  };
}

function normalizeSitesList(data: unknown): DudaSiteSummary[] {
  if (Array.isArray(data)) {
    return data.map(normalizeSiteRow).filter(Boolean) as DudaSiteSummary[];
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    // Partner API list: https://developer.duda.co/reference/sites-list-sites
    if (Array.isArray(o.results)) {
      return o.results.map(normalizeSiteRow).filter(Boolean) as DudaSiteSummary[];
    }
    if (Array.isArray(o.sites)) {
      return o.sites.map(normalizeSiteRow).filter(Boolean) as DudaSiteSummary[];
    }
  }
  return [];
}

export async function dudaRequest<T = unknown>(
  apiUser: string,
  apiPassword: string,
  path: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const base = getDudaApiBase();
  const p = path.startsWith('/') ? path : `/${path}`;
  const { data } = await axios.request<T>({
    url: `${base}${p}`,
    method: config?.method ?? 'GET',
    ...config,
    headers: {
      Authorization: dudaAuthHeader(apiUser, apiPassword),
      Accept: 'application/json',
      ...config?.headers,
    },
  });
  return data;
}

/** Partner API: list sites (paginated `{ results, total_responses }` or legacy array). */
export async function listDudaSites(apiUser: string, apiPassword: string): Promise<DudaSiteSummary[]> {
  const paths = ['/sites/multiscreen', '/sites'];
  let lastErr: unknown;
  for (const p of paths) {
    try {
      const merged: DudaSiteSummary[] = [];
      let offset = 0;
      const limit = 100;

      while (true) {
        const data = await dudaRequest<unknown>(
          apiUser,
          apiPassword,
          p,
          p === '/sites/multiscreen'
            ? { params: { offset, limit, sort: 'CREATION_DATE', direction: 'DESC' } }
            : undefined
        );
        const batch = normalizeSitesList(data);
        merged.push(...batch);

        const total =
          data && typeof data === 'object' && typeof (data as { total_responses?: unknown }).total_responses === 'number'
            ? (data as { total_responses: number }).total_responses
            : null;

        if (p !== '/sites/multiscreen' || total == null) {
          break;
        }
        if (batch.length === 0 || merged.length >= total) {
          break;
        }
        offset += limit;
      }

      return merged;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Could not list Duda sites');
}

/** Partner API: get one site — path uses `site_name` per docs. */
export async function getDudaSiteDetail(
  apiUser: string,
  apiPassword: string,
  siteName: string
): Promise<unknown> {
  const enc = encodeURIComponent(siteName.trim());
  return dudaRequest(apiUser, apiPassword, `/sites/multiscreen/${enc}`);
}

/** Flattened fields from `GET /sites/multiscreen/{site_name}` for tables and filters. */
export type DudaSiteDetailRow = {
  site_name: string;
  site_domain: string;
  site_default_domain?: string;
  canonical_url?: string;
  account_name?: string;
  external_uid?: string;
  publish_status?: string;
  editor?: string;
  store_status?: string;
  store_type?: string;
  certificate_status?: string;
  last_published_date?: string;
  first_published_date?: string;
  creation_date?: string;
  modification_date?: string;
  template_id?: number;
  lang?: string;
  labels: string[];
  booking_status?: string;
  force_https?: boolean;
  overview_site_url?: string;
  editor_site_url?: string;
  preview_site_url?: string;
  thumbnail_url?: string;
  google_tracking_id?: string;
  /** Present when the Get Site request failed */
  detailLoadError?: string;
};

function readStr(o: Record<string, unknown>, key: string): string | undefined {
  const v = o[key];
  if (v == null) {
    return undefined;
  }
  const s = String(v).trim();
  return s || undefined;
}

function readLabelNames(o: Record<string, unknown>): string[] {
  const raw = o.labels;
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: string[] = [];
  for (const x of raw) {
    if (x && typeof x === 'object' && 'name' in x) {
      const n = String((x as { name: unknown }).name).trim();
      if (n) {
        out.push(n);
      }
    }
  }
  return out;
}

/** Map Get Site JSON into a table row; returns null if `site_name` is missing. */
export function parseDudaSiteDetail(raw: unknown): DudaSiteDetailRow | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const site_name = readStr(o, 'site_name');
  if (!site_name) {
    return null;
  }
  const booking = o.booking;
  let booking_status: string | undefined;
  if (booking && typeof booking === 'object') {
    booking_status = readStr(booking as Record<string, unknown>, 'status');
  }
  const tid = o.template_id;
  let template_id: number | undefined;
  if (typeof tid === 'number' && !Number.isNaN(tid)) {
    template_id = tid;
  } else if (typeof tid === 'string' && tid.trim()) {
    const n = Number(tid);
    if (!Number.isNaN(n)) {
      template_id = n;
    }
  }

  return {
    site_name,
    site_domain: readStr(o, 'site_domain') ?? '',
    site_default_domain: readStr(o, 'site_default_domain'),
    canonical_url: readStr(o, 'canonical_url'),
    account_name: readStr(o, 'account_name'),
    external_uid: readStr(o, 'external_uid'),
    publish_status: readStr(o, 'publish_status'),
    editor: readStr(o, 'editor'),
    store_status: readStr(o, 'store_status'),
    store_type: readStr(o, 'store_type'),
    certificate_status: readStr(o, 'certificate_status'),
    last_published_date: readStr(o, 'last_published_date'),
    first_published_date: readStr(o, 'first_published_date'),
    creation_date: readStr(o, 'creation_date'),
    modification_date: readStr(o, 'modification_date'),
    template_id: template_id != null && !Number.isNaN(template_id) ? template_id : undefined,
    lang: readStr(o, 'lang'),
    labels: readLabelNames(o),
    booking_status,
    force_https: typeof o.force_https === 'boolean' ? o.force_https : undefined,
    overview_site_url: readStr(o, 'overview_site_url'),
    editor_site_url: readStr(o, 'editor_site_url'),
    preview_site_url: readStr(o, 'preview_site_url'),
    thumbnail_url: readStr(o, 'thumbnail_url'),
    google_tracking_id: readStr(o, 'google_tracking_id'),
  };
}

function detailRowFromSummary(s: DudaSiteSummary): DudaSiteDetailRow {
  return {
    site_name: s.site_name,
    site_domain: s.site_domain,
    account_name: s.account_name,
    external_uid: s.external_uid,
    labels: [],
  };
}

function mergeSummaryIntoDetail(d: DudaSiteDetailRow, s: DudaSiteSummary): DudaSiteDetailRow {
  return {
    ...d,
    site_domain: d.site_domain || s.site_domain,
    account_name: d.account_name || s.account_name,
    external_uid: d.external_uid || s.external_uid,
  };
}

async function runPool<T, R>(items: T[], limit: number, worker: (item: T, index: number) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  async function runWorker() {
    while (true) {
      const i = next;
      next += 1;
      if (i >= items.length) {
        return;
      }
      out[i] = await worker(items[i], i);
    }
  }
  const workers = Math.min(Math.max(1, limit), Math.max(1, items.length));
  await Promise.all(Array.from({ length: workers }, () => runWorker()));
  return out;
}

/**
 * Fetches `GET /sites/multiscreen/{site_name}` for each listed site (concurrency-limited).
 * Returns parsed rows plus raw JSON keyed by `site_name` for detail panels without a second GET.
 */
export async function fetchDudaSiteDetailRows(
  apiUser: string,
  apiPassword: string,
  summaries: DudaSiteSummary[],
  concurrency = 5
): Promise<{ rows: DudaSiteDetailRow[]; rawBySite: Record<string, unknown> }> {
  const rawBySite: Record<string, unknown> = {};
  const rows = await runPool(summaries, concurrency, async (summary) => {
    try {
      const raw = await getDudaSiteDetail(apiUser, apiPassword, summary.site_name);
      if (raw && typeof raw === 'object') {
        rawBySite[summary.site_name] = raw;
      }
      const parsed = parseDudaSiteDetail(raw);
      if (parsed) {
        return mergeSummaryIntoDetail(parsed, summary);
      }
    } catch {
      /* use fallback below */
    }
    return { ...detailRowFromSummary(summary), detailLoadError: 'Could not load site detail' };
  });
  return { rows, rawBySite };
}

export async function fetchDudaSiteAnalytics(
  apiUser: string,
  apiPassword: string,
  siteIdOrName: string,
  options?: {
    from?: string;
    to?: string;
    result?: string;
    dateGranularity?: DudaDateGranularity;
  }
): Promise<unknown> {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 3);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const from = options?.from ?? fmt(start);
  const to = options?.to ?? fmt(end);
  const enc = encodeURIComponent(siteIdOrName.trim());

  const config: AxiosRequestConfig = {
    params: {
      from,
      to,
      result: options?.result ?? 'activities',
      dateGranularity: options?.dateGranularity ?? 'MONTHS',
    },
  };

  return dudaRequest(apiUser, apiPassword, `/analytics/site/${enc}`, config);
}

/**
 * Full analytics report per Duda docs: `result` is `traffic` or `activities`.
 * Do not pass `dimension` with `activities` (API error).
 * @see https://developer.duda.co/reference/reporting-analytics
 */
export async function fetchDudaSiteAnalyticsReport(
  apiUser: string,
  apiPassword: string,
  siteName: string,
  options: {
    from: string;
    to: string;
    result: 'traffic' | 'activities';
    dateGranularity: DudaDateGranularity;
    dimension?: 'geo' | 'system';
  }
): Promise<unknown> {
  const enc = encodeURIComponent(siteName.trim());
  const params: Record<string, string> = {
    from: options.from,
    to: options.to,
    result: options.result,
    dateGranularity: options.dateGranularity,
  };
  if (options.result === 'traffic' && options.dimension) {
    params.dimension = options.dimension;
  }
  return dudaRequest(apiUser, apiPassword, `/analytics/site/${enc}`, { params });
}

/** Reporting-style traffic (alternate `result`); Duda may return different shapes. */
export async function fetchDudaSiteTrafficAnalytics(
  apiUser: string,
  apiPassword: string,
  siteIdOrName: string,
  options?: { from?: string; to?: string; dateGranularity?: DudaDateGranularity }
): Promise<unknown> {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 3);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const from = options?.from ?? fmt(start);
  const to = options?.to ?? fmt(end);
  const enc = encodeURIComponent(siteIdOrName.trim());
  return dudaRequest(apiUser, apiPassword, `/analytics/site/${enc}`, {
    params: {
      from,
      to,
      result: 'traffic',
      dateGranularity: options?.dateGranularity ?? 'MONTHS',
    },
  });
}

const pickNum = (x: unknown): number | undefined =>
  typeof x === 'number' && !Number.isNaN(x) ? x : undefined;

function effectiveRow(row: Record<string, unknown>): Record<string, unknown> {
  const inner = row.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return { ...row, ...(inner as Record<string, unknown>) };
  }
  return row;
}

function formatDimLabel(dim: unknown): string {
  if (!dim || typeof dim !== 'object') {
    return '';
  }
  return Object.entries(dim as Record<string, unknown>)
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(' · ');
}

/** Extract array rows from Duda analytics JSON (nested keys differ by endpoint / version). */
export function extractDudaAnalyticsRows(data: unknown): Record<string, unknown>[] {
  if (data == null) {
    return [];
  }
  if (Array.isArray(data)) {
    return data.filter((x): x is Record<string, unknown> => x != null && typeof x === 'object' && !Array.isArray(x));
  }
  if (typeof data !== 'object') {
    return [];
  }
  const o = data as Record<string, unknown>;
  const raw = o.activities ?? o.results ?? o.data ?? o.time_series ?? o.series ?? o.rows;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((x): x is Record<string, unknown> => x != null && typeof x === 'object' && !Array.isArray(x));
}

export type DudaChartMetric =
  | 'visitors'
  | 'visits'
  | 'pageviews'
  | 'form_submits'
  | 'click_to_calls'
  | 'click_to_maps'
  | 'click_to_emails';

function pickChartMetric(e: Record<string, unknown>, metric: DudaChartMetric): number | undefined {
  switch (metric) {
    case 'visitors':
      return (
        pickNum(e.VISITORS) ??
        pickNum(e.visitors) ??
        pickNum(e.unique_visitors) ??
        pickNum(e.VISITOR)
      );
    case 'visits':
      return pickNum(e.VISITS) ?? pickNum(e.visits) ?? pickNum(e.sessions);
    case 'pageviews':
      return pickNum(e.PAGE_VIEWS) ?? pickNum(e.pageviews) ?? pickNum(e.PAGEVIEWS);
    case 'form_submits':
      return pickNum(e.FORM_SUBMITS) ?? pickNum(e.form_submits);
    case 'click_to_calls':
      return pickNum(e.CLICK_TO_CALLS) ?? pickNum(e.click_to_calls);
    case 'click_to_maps':
      return pickNum(e.CLICK_TO_MAPS) ?? pickNum(e.click_to_maps);
    case 'click_to_emails':
      return pickNum(e.CLICK_TO_EMAILS) ?? pickNum(e.click_to_emails);
    default:
      return undefined;
  }
}

function rowLabel(row: Record<string, unknown>, index: number): string {
  const e = effectiveRow(row);
  const fromTime =
    e.date ?? e.period ?? e.month ?? e.week ?? e.day ?? e.time ?? e.year ?? e.label ?? e.bucket;
  if (fromTime != null && String(fromTime).trim()) {
    return String(fromTime);
  }
  const dim = row.dimension ?? e.dimension;
  const dlab = formatDimLabel(dim);
  if (dlab) {
    return dlab;
  }
  return `Row ${index + 1}`;
}

/**
 * Time series / breakdown for charts. Handles Duda’s uppercase traffic fields (`VISITORS`, `VISITS`, `PAGE_VIEWS`),
 * nested `{ data: { ... } }` rows, dimension-only rows, and flat totals.
 */
export function parseDudaSeries(data: unknown, metric: DudaChartMetric = 'visitors'): { label: string; value: number }[] {
  const rows = extractDudaAnalyticsRows(data);
  const out: { label: string; value: number }[] = [];

  if (rows.length > 0) {
    rows.forEach((row, i) => {
      const e = effectiveRow(row);
      const v = pickChartMetric(e, metric);
      if (v != null) {
        out.push({ label: rowLabel(row, i), value: v });
      }
    });
    if (out.length > 0) {
      return out;
    }
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const o = data as Record<string, unknown>;
    const v = pickChartMetric(o, metric);
    if (v != null) {
      return [{ label: 'Period total', value: v }];
    }
  }

  return out;
}

/** Stacked-style breakdown when Duda returns a single activities object (no time series). */
export function parseDudaActivitiesBreakdown(data: unknown): { label: string; value: number }[] {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) {
    return [];
  }
  const o = data as Record<string, unknown>;
  const pairs: [string, DudaChartMetric][] = [
    ['Form submits', 'form_submits'],
    ['Click to call', 'click_to_calls'],
    ['Click to map', 'click_to_maps'],
    ['Click to email', 'click_to_emails'],
  ];
  const out: { label: string; value: number }[] = [];
  for (const [label, m] of pairs) {
    const v = pickChartMetric(o, m);
    if (v != null && v > 0) {
      out.push({ label, value: v });
    }
  }
  return out;
}

/** Best-effort parse — Duda payload shapes vary by `result` / version. */
export function parseDudaActivityVisits(data: unknown): number | null {
  if (data == null || typeof data !== 'object') {
    return null;
  }
  const o = data as Record<string, unknown>;

  const direct =
    pickNum(o.visits) ??
    pickNum(o.unique_visitors) ??
    pickNum(o.VISITORS) ??
    pickNum(o.VISITS) ??
    pickNum(o.total_visits) ??
    pickNum(o.sessions) ??
    pickNum(o.PAGE_VIEWS);
  if (direct != null) {
    return direct;
  }

  const engagement =
    pickNum(o.FORM_SUBMITS) ??
    pickNum(o.CLICK_TO_CALLS) ??
    pickNum(o.CLICK_TO_MAPS) ??
    pickNum(o.CLICK_TO_EMAILS);
  if (engagement != null) {
    return engagement;
  }

  const rows = o.activities ?? o.results ?? o.data;
  if (!Array.isArray(rows)) {
    return null;
  }

  let sum = 0;
  let n = 0;
  for (const row of rows) {
    if (!row || typeof row !== 'object') {
      continue;
    }
    const r = effectiveRow(row as Record<string, unknown>);
    const v =
      pickNum(r.unique_visitors) ??
      pickNum(r.visits) ??
      pickNum(r.VISITORS) ??
      pickNum(r.VISITS) ??
      pickNum(r.sessions) ??
      pickNum(r.pageviews) ??
      pickNum(r.PAGE_VIEWS) ??
      pickNum(r.total) ??
      pickNum(r.FORM_SUBMITS);
    if (v != null) {
      sum += v;
      n += 1;
    }
  }
  return n > 0 ? sum : null;
}
